<template>
    <!-- The title lives on the wrapper: a disabled button fires no mouse
         event, so the browser would never show its own tooltip. -->
    <span class="br-wrap" :title="tooltip">
        <button
            type="button"
            class="br-btn"
            :class="{
                'br-btn--minimal': variant === 'minimal',
                'br-btn--preview': variant === 'preview',
                'br-btn--muted': !hasOperator
            }"
            @click.prevent="sendRemindToOperator"
            :disabled="loading || !hasOperator"
            :aria-label="tooltip"
        >
            <i v-if="loading" class="fal fa-spinner-third fa-spin br-btn-icon"></i>
            <i v-else class="fal fa-bell br-btn-icon"></i>
            <span v-if="variant !== 'minimal'" class="br-btn-text">
                {{ $t('post.page.button.restart') }}
            </span>
        </button>
    </span>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import { mapActions } from 'vuex'

export default {
    computed: {
        // A ticket can perfectly well have RACIs without anyone being the
        // operator (is_responsible), and the list payload may omit racis
        // entirely — both cases mean there is nobody to nudge.
        hasOperator() {
            const racis = this.post && this.post.racis
            if (!Array.isArray(racis)) return false
            return racis.some((raci) => raci.is_responsible == 1)
        },
        tooltip() {
            return this.hasOperator
                ? this.$t('post.page.button.restart')
                : this.$t('post.page.button.restart_no_operator')
        }
    },
    props: ['post', 'variant'],
    data() {
        return {
            loading: false
        }
    },
    methods: {
        ...mapActions('post', ['remindOperator', 'getPostHistories']),
        sendRemindToOperator() {
            if (!this.hasOperator || this.loading) return
            this.loading = true
            this.remindOperator(this.post.id)
                .then(() => {
                    this.getPostHistories(this.post.id)
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('post.operator.reminded')
                    })
                })
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    setTimeout(() => {
                        this.loading = false
                    }, 1000)
                })
        }
    }
}
</script>

<style scoped>
.br-wrap {
    display: inline-flex;
}

.br-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 8px 16px;
    background: #f59e0b;
    border: none;
    border-radius: 8px;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.br-btn:hover:not(:disabled) {
    background: #d97706;
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(245, 158, 11, 0.3);
}

.br-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
}

.br-btn-icon {
    font-size: 0.82rem;
    color: #fff !important;
}

.br-btn--minimal {
    width: 32px;
    height: 32px;
    padding: 0;
    border-radius: 8px;
}

.br-btn--minimal .br-btn-icon {
    font-size: 0.78rem;
}

/* Preview variant — used inside the ticket quick-view drawer, solid
 * warning (amber) fill to signal the nudging / recall semantic. */
.br-btn--preview {
    padding: 7px 12px;
    border: 1px solid var(--v2-warning, #f59e0b);
    background: var(--v2-warning, #f59e0b);
    color: #fff;
    font-size: 0.8125rem;
    font-weight: 500;
    border-radius: var(--v2-radius-md, 8px);
    transition: all 120ms ease;
}

.br-btn--preview .br-btn-icon {
    color: #fff !important;
    font-size: 0.85rem;
}

.br-btn--preview:hover:not(:disabled) {
    filter: brightness(0.95);
    transform: none;
    box-shadow: none;
}

/* No operator assigned: the action is impossible, so the button is greyed
 * out rather than hidden — hiding it made the row jump around. */
.br-btn--muted,
.br-btn--muted.br-btn--preview {
    background: var(--v2-surface-2, #f1f5f9);
    border: 1px solid var(--v2-border, #e2e8f0);
    color: var(--v2-text-4, #94a3b8);
}

.br-btn--muted .br-btn-icon {
    color: var(--v2-text-4, #94a3b8) !important;
}

.br-btn--muted:disabled {
    opacity: 1;
}
</style>
